import { useState, Fragment, useEffect } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
    MediumHeightDivider,
    SmallHeightDivider,
    StyledButtonOutlined,
    StyledButton,
} from "../../../../theme/Styles";
import { useHistory, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { FormCompanySchema } from "./MyProfileConstants";
import {
    ProfileGrid,
    LeftCol,
    RightCol,
    UserCard,
    AvatarContainer,
    AvatarFrame,
    ProfileImage,
    CameraOverlay,
    UserName,
    UserRole,
    DividerLine,
    ActionButton,
    StatusCard,
    StatusTitle,
    StatusRow,
    StatusLabel,
    StatusValue,
    BadgeNumber,
    DataCard,
    CardHeaderBar,
    HeaderTitleWrapper,
    CardHeaderTitle,
    VerifiedBadge,
    DataBody,
    DataGrid,
    DataField,
    FieldLabel,
    FieldValue,
    CompanyInfoBox,
    CompanyIconSquare,
    CompanyTextWrapper,
    RncBadge,
    LocationRow,
    QuickAccessGrid,
    QuickAccessCard,
    QuickTitle,
    QuickDesc,
} from "./styles/MyProfileStyles";
import Fade from "react-reveal/Fade";
import { useFormik } from "formik";
import TextField from "../../../../components/TextField/TextField";
import PhoneTextField from "../../../../components/PhoneTextField/PhoneTextField";
import { Grid, Box, Typography, Divider, Button } from "@mui/material";
import FormModal from "../../../../components/FormModal/FormModal";
import { SectionTitle, SectionTextDivider } from "../../styles/MyDeskStyles";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { addNewCompany, getAllCompanies, modifyCompany } from "../../../../api/myCompany";
import { getUser } from "../../../../api/Auth";
import { getMetricsData } from "../../../../api/myDesk";
import CenterLoading from "../../../../components/CenterLoading/CenterLoading";
import EditIcon from "@mui/icons-material/Edit";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BarChartIcon from "@mui/icons-material/BarChart";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { stringToDominicanCedula, stringToDominicanPhoneNumber } from "../../../../utilities/functions/FormatterUtil";
import { cleanStringFromNumbers } from "../../../../utilities/functions/NumberUtil";
import LocalStorageService from "../../../../services/LocalStorageService";
import { ROLE_TYPE } from "../../../../constants/constants";

function MyProfile({ rolBase }) {
    const matchesWidth = useMediaQuery("(min-width:768px)");
    const history = useHistory();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const [userImage, setUserImage] = useState("");
    const location = useLocation();

    const [openModifyOrAddCompanyModal, setOpenModifyOrAddCompanyModal] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState();

    const userQuery = useQuery(["userData"], () => getUser());
    const companiesQuery = useQuery(["allCompaniesData"], () => getAllCompanies());
    const { data: metricsData } = useQuery(
        ["myDeskMetricsData"],
        () => getMetricsData(userQuery.data?.payload?.citizen_id),
        {
            enabled: userQuery.data?.payload?.citizen_id !== undefined,
            cacheTime: 0,
            staleTime: 0,
        }
    );
    const mutationForModifyCompany = useMutation(modifyCompany);
    const mutationForAddCompany = useMutation(addNewCompany);

    const registerCompany = (formData) => {
        mutationForAddCompany.mutate(formData, {
            onSuccess: (data) => {
                if (data.success) {
                    // refresh cache of allCompaniesData
                    queryClient.invalidateQueries("allCompaniesData");
                }
            },
        });
    };

    const modifySelectedCompany = (formData) => {
        mutationForModifyCompany.mutate(formData, {
            onSuccess: (data) => {
                if (data.success) {
                    // refresh cache of allCompaniesData
                    queryClient.invalidateQueries("allCompaniesData");
                }
            },
        });
    };

    const companyFormik = useFormik({
        initialValues: {
            company_rnc: "",
            company_phone: "",
            company_name: "",
            company_address: "",
            company_url_web: "",
        },
        validationSchema: FormCompanySchema,
        onSubmit: (values) => {
            if (selectedCompany) {
                modifySelectedCompany(values);
            } else {
                registerCompany(values);
            }
            handleModifyOrAddCompanyModal();
        },
    });

    const handleModifyOrAddCompanyModal = (company) => {
        if (company) {
            setSelectedCompany(company);
            Object.keys(companyFormik.values).map((key) => {
                return companyFormik.setFieldValue(key, company[key], false);
            });
        } else {
            setSelectedCompany(null);
            Object.keys(companyFormik.values).map((key) => {
                return companyFormik.setFieldValue(key, "", false);
            });
        }
        setOpenModifyOrAddCompanyModal(!openModifyOrAddCompanyModal);
    };

    const [currentLang, setCurrentLang] = useState(LocalStorageService.getItem('language') || 'es');

    useEffect(() => {
        const handleLangChange = (e) => {
            if (e?.detail) {
                setCurrentLang(e.detail);
            } else {
                setCurrentLang(LocalStorageService.getItem('language') || 'es');
            }
        };
        window.addEventListener('languageChange', handleLangChange);
        return () => window.removeEventListener('languageChange', handleLangChange);
    }, []);

    const isEn = currentLang === 'en';

    useEffect(() => {
        setUserImage(LocalStorageService.getItem("profile_img"));
    }, []);

    if (userQuery.isLoading || companiesQuery.isLoading) {
        return <CenterLoading />;
    }

    const company = companiesQuery.data?.payload?.[0];
    const pendingCount = metricsData?.pending || 0;
    const inProcessCount = metricsData?.inProcess || 0;
    const activeCount = pendingCount + inProcessCount;

    return (
        <Fade right>
            <Fragment>
                <ProfileGrid>
                    {/* Left Column */}
                    <LeftCol>
                        <UserCard>
                            <AvatarContainer>
                                <AvatarFrame>
                                    <ProfileImage src={userImage || "https://via.placeholder.com/150"} alt="User Avatar" />
                                </AvatarFrame>
                                <CameraOverlay>
                                    <CameraAltIcon />
                                </CameraOverlay>
                            </AvatarContainer>
                            <UserName>
                                {userQuery.data?.payload
                                    ? `${userQuery.data.payload.name} ${userQuery.data.payload.first_last_name}`
                                    : "-"}
                            </UserName>
                            <UserRole>
                                {rolBase === ROLE_TYPE.COMPANY 
                                  ? (isEn ? "INVESTMENT MANAGER" : "ADMINISTRADOR DE INVERSIONES") 
                                  : (isEn ? "CITIZEN / APPLICANT" : "CIUDADANO / SOLICITANTE")}
                            </UserRole>
                            
                            <DividerLine />
                            
                            <ActionButton variant="primary" onClick={() => history.push("/app/mi-cuenta/contacto")}>
                                <EditIcon /> {isEn ? "Edit Profile" : "Editar Perfil"}
                            </ActionButton>
                            <ActionButton variant="secondary" onClick={() => history.push("/app/mi-cuenta/seguridad", { openPasswordModal: true })}>
                                <VpnKeyIcon /> {isEn ? "Change Password" : "Cambiar Clave"}
                            </ActionButton>
                        </UserCard>

                        <StatusCard>
                            <StatusTitle>{isEn ? "Account Status" : "Estado de Cuenta"}</StatusTitle>
                            <StatusRow>
                                <StatusLabel>{isEn ? "Last access" : "Último acceso"}</StatusLabel>
                                <StatusValue>{isEn ? "2 hours ago" : "Hace 2 horas"}</StatusValue>
                            </StatusRow>
                            <StatusRow>
                                <StatusLabel>{isEn ? "Active procedures" : "Trámites activos"}</StatusLabel>
                                <BadgeNumber>{activeCount}</BadgeNumber>
                            </StatusRow>
                        </StatusCard>
                    </LeftCol>

                    {/* Right Column */}
                    <RightCol>
                        <DataCard>
                            <CardHeaderBar bgType="light">
                                <HeaderTitleWrapper>
                                    <AssignmentIcon />
                                    <CardHeaderTitle>{isEn ? "Personal Information" : "Datos Personales"}</CardHeaderTitle>
                                </HeaderTitleWrapper>
                                <VerifiedBadge>{isEn ? "Verified" : "Verificado"}</VerifiedBadge>
                            </CardHeaderBar>
                            <DataBody>
                                <DataGrid>
                                    <DataField>
                                        <FieldLabel>{isEn ? "Full Name" : "Nombre Completo"}</FieldLabel>
                                        <FieldValue>
                                            {userQuery.data?.payload
                                                ? `${userQuery.data.payload.name} ${userQuery.data.payload.first_last_name} ${userQuery.data.payload.second_last_name || ""}`
                                                : "-"}
                                        </FieldValue>
                                    </DataField>
                                    <DataField>
                                        <FieldLabel>{isEn ? "Identity Document" : "Documento de Identidad"}</FieldLabel>
                                        <FieldValue>
                                            {userQuery.data?.payload
                                                ? stringToDominicanCedula(userQuery.data.payload.citizen_id)
                                                : "-"}
                                        </FieldValue>
                                    </DataField>
                                    <DataField>
                                        <FieldLabel>{isEn ? "City of Residence" : "Ciudad de Residencia"}</FieldLabel>
                                        <FieldValue>
                                            {userQuery.data?.payload?.province || "Santo Domingo, DN"}
                                        </FieldValue>
                                    </DataField>
                                    <DataField>
                                        <FieldLabel>{isEn ? "Contact Phone" : "Teléfono de Contacto"}</FieldLabel>
                                        <FieldValue>
                                            {userQuery.data?.payload
                                                ? stringToDominicanPhoneNumber(userQuery.data.payload.phone)
                                                : "-"}
                                        </FieldValue>
                                    </DataField>
                                </DataGrid>
                                <Divider sx={{ my: 1 }} />
                                <DataField>
                                    <FieldLabel>{isEn ? "Email Address" : "Correo Electrónico"}</FieldLabel>
                                    <FieldValue>
                                        {userQuery.data?.payload?.email || "-"}
                                        <CheckCircleIcon sx={{ ml: 1, color: '#10B981', fontSize: '18px' }} />
                                    </FieldValue>
                                </DataField>
                            </DataBody>
                        </DataCard>

                        <DataCard>
                            <CardHeaderBar bgType="navy">
                                <HeaderTitleWrapper>
                                    <BusinessIcon />
                                    <CardHeaderTitle>{isEn ? "Institutional / Company Information" : "Información Institucional / Empresa"}</CardHeaderTitle>
                                </HeaderTitleWrapper>
                                {company && (
                                    <Button 
                                        onClick={() => handleModifyOrAddCompanyModal(company)}
                                        sx={{ 
                                            color: '#FFFFFF', 
                                            p: 0, 
                                            minWidth: 'auto', 
                                            textDecoration: 'underline',
                                            textTransform: 'none',
                                            fontFamily: 'inherit',
                                            fontSize: '13px',
                                            fontWeight: '700',
                                            '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' }
                                        }}
                                    >
                                        {isEn ? "Edit Company" : "Editar Empresa"}
                                    </Button>
                                )}
                            </CardHeaderBar>
                            <DataBody>
                                {company ? (
                                    <Fragment>
                                        <CompanyInfoBox>
                                            <CompanyIconSquare>
                                                <BusinessIcon />
                                            </CompanyIconSquare>
                                            <CompanyTextWrapper>
                                                <FieldLabel>{isEn ? "Company Name" : "Nombre de la Empresa"}</FieldLabel>
                                                <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#192873' }}>
                                                    {company.company_name}
                                                </h4>
                                                <span style={{ fontSize: '12px', color: '#767680', fontStyle: 'italic' }}>
                                                    {isEn ? "Consolidated Commercial Registry" : "Registro Comercial Consolidado"}
                                                </span>
                                            </CompanyTextWrapper>
                                        </CompanyInfoBox>

                                        <DataGrid>
                                            <DataField>
                                                <FieldLabel>{isEn ? "RNC (Taxpayer Registration Number)" : "RNC (Registro Nacional de Contribuyente)"}</FieldLabel>
                                                <div>
                                                    <RncBadge>{company.company_rnc}</RncBadge>
                                                </div>
                                            </DataField>
                                            <DataField>
                                                <FieldLabel>{isEn ? "Economic Sector" : "Sector Económico"}</FieldLabel>
                                                <FieldValue>
                                                    <BusinessIcon sx={{ color: '#B7102A' }} /> {isEn ? "Tourism / Hospitality" : "Turismo / Hotelería"}
                                                </FieldValue>
                                            </DataField>
                                        </DataGrid>
                                        <Divider sx={{ my: 1 }} />
                                        <DataField>
                                            <FieldLabel>{isEn ? "Head Office Address" : "Dirección de Oficina Principal"}</FieldLabel>
                                            <LocationRow>
                                                <LocationOnIcon />
                                                <FieldValue style={{ fontSize: '13px', fontWeight: '500' }}>
                                                    {company.company_address}
                                                </FieldValue>
                                            </LocationRow>
                                        </DataField>
                                    </Fragment>
                                ) : (
                                    <Box sx={{ textAlign: 'center', py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                        <BusinessIcon sx={{ fontSize: '48px', color: '#767680', opacity: 0.5 }} />
                                        <Typography variant="body1" sx={{ color: '#767680', fontWeight: '600' }}>
                                            {isEn ? "No companies linked to your account" : "No hay empresas asociadas a su cuenta"}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#767680', maxWidth: '360px', mb: 2 }}>
                                            {isEn
                                              ? "If you represent an institution or tourism company, link it to manage corporate applications."
                                              : "Si representa a una institución o empresa turística, vincúlela para gestionar solicitudes corporativas."}
                                        </Typography>
                                        <StyledButtonOutlined 
                                            variant="outlined" 
                                            onClick={() => handleModifyOrAddCompanyModal()}
                                            style={{ width: 'auto', padding: '8px 24px' }}
                                        >
                                            {isEn ? "Add Company" : "Agregar Empresa"}
                                        </StyledButtonOutlined>
                                    </Box>
                                )}
                            </DataBody>
                        </DataCard>

                        <QuickAccessGrid>
                            <QuickAccessCard onClick={() => history.push("/app/mi-escritorio?tab=1")}>
                                <AssignmentIcon />
                                <QuickTitle>{isEn ? "Procedures" : "Trámites"}</QuickTitle>
                                <QuickDesc>{pendingCount} {isEn ? "Pending requests" : "Solicitudes pendientes"}</QuickDesc>
                            </QuickAccessCard>
                            <QuickAccessCard onClick={() => history.push("/todos-los-tramites?categoria=tramitacion-y-evaluacion-de-proyectos-dpp")}>
                                <BarChartIcon />
                                <QuickTitle>DPP</QuickTitle>
                                <QuickDesc>{isEn ? "Project management" : "Gestión de proyectos"}</QuickDesc>
                            </QuickAccessCard>
                            <QuickAccessCard onClick={() => history.push("/todos-los-tramites?categoria=clasificaciones-de-confotur")}>
                                <AssignmentTurnedInIcon />
                                <QuickTitle>CONFOTUR</QuickTitle>
                                <QuickDesc>{isEn ? "Active classifications" : "Clasificaciones activas"}</QuickDesc>
                            </QuickAccessCard>
                        </QuickAccessGrid>
                    </RightCol>
                </ProfileGrid>
                <FormModal
                    onClose={() => handleModifyOrAddCompanyModal()}
                    open={openModifyOrAddCompanyModal}
                    title={selectedCompany ? "Modificar empresa" : "Agregar empresa"}
                >
                    <SmallHeightDivider />
                    <Grid
                        alignItems="flex-start"
                        justifyContent="center"
                        container
                        direction="row"
                        spacing={{ xs: 2, md: 3 }}
                        columns={{ xs: 4, sm: 8, md: 12 }}
                    >
                        <Grid item xs={4} sm={8} md={12}>
                            <TextField
                                title="Nombre comercial"
                                type="text"
                                id="company_name"
                                value={companyFormik.values.company_name}
                                onChange={companyFormik.handleChange}
                                error={companyFormik.touched.company_name && Boolean(companyFormik.errors.company_name)}
                                helperText={companyFormik.touched.company_name && companyFormik.errors.company_name}
                                required
                            />
                        </Grid>

                        <Grid item xs={4} sm={4} md={6}>
                            <TextField
                                title="RNC"
                                type="text"
                                id="company_rnc"
                                required
                                disabled={selectedCompany ? true : false}
                                mask="9-99-99999-9"
                                unMaskedValue
                                value={companyFormik.values.company_rnc}
                                onChange={companyFormik.handleChange}
                                error={companyFormik.touched.company_rnc && Boolean(companyFormik.errors.company_rnc)}
                                helperText={companyFormik.touched.company_rnc && companyFormik.errors.company_rnc}
                            />
                        </Grid>

                        <Grid item xs={4} sm={4} md={6}>
                            <PhoneTextField
                                title="Teléfono"
                                type="text"
                                id="company_phone"
                                required
                                value={cleanStringFromNumbers(companyFormik.values.company_phone)}
                                onChange={companyFormik.handleChange}
                                error={companyFormik.touched.company_phone && Boolean(companyFormik.errors.company_phone)}
                                helperText={companyFormik.touched.company_phone && companyFormik.errors.company_phone}
                            />
                        </Grid>

                        <Grid item xs={4} sm={8} md={12}>
                            <TextField
                                title="Dirección"
                                type="text"
                                id="company_address"
                                value={companyFormik.values.company_address}
                                onChange={companyFormik.handleChange}
                                error={companyFormik.touched.company_address && Boolean(companyFormik.errors.company_address)}
                                helperText={companyFormik.touched.company_address && companyFormik.errors.company_address}
                                required
                            />
                        </Grid>

                        <Grid item xs={4} sm={8} md={12}>
                            <TextField
                                title="Web"
                                type="text"
                                id="company_url_web"
                                value={companyFormik.values.company_url_web}
                                onChange={companyFormik.handleChange}
                                error={companyFormik.touched.company_url_web && Boolean(companyFormik.errors.company_url_web)}
                                helperText={companyFormik.touched.company_url_web && companyFormik.errors.company_url_web}
                            />
                        </Grid>
                    </Grid>
                    <SmallHeightDivider />
                    <StyledButton onClick={() => companyFormik.handleSubmit()}>CONFIRMAR</StyledButton>
                    <SmallHeightDivider />
                </FormModal>
            </Fragment>
        </Fade>
    );
}

export default MyProfile;
